/**
 * <quote-request-modal>
 * Antes de abrir el modal, sincroniza sus datos con la variante
 * REALMENTE seleccionada en ese momento -- leyendo el input oculto
 * que el propio formulario de compra nativo mantiene actualizado
 * (name="id" ref="variantId"), y buscando esa variante en la API
 * pública JSON del producto (/products/<handle>.js). No depende de
 * ningún evento personalizado del selector de variantes del tema,
 * así que funciona sin importar cómo esté implementado.
 */
class QuoteRequestModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.closest('dialog');
    this.form = this.querySelector('form');
    this.successEl = this.querySelector('[data-quote-success]');
    this.handle = this.dataset.productHandle;
    this.variantsPromise = null;

    this.closeBtn = this.querySelector('[data-quote-modal-close]');
    this.closeBtn?.addEventListener('click', () => this.dialog.close());

    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });

    this.form?.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  // Devuelve (y cachea) la lista de variantes del producto vía la
  // API JSON pública de Shopify. Se pide una sola vez por página.
  async getVariants() {
    if (!this.variantsPromise) {
      this.variantsPromise = fetch(`/products/${this.handle}.js`)
        .then((r) => r.json())
        .then((data) => data.variants || [])
        .catch((err) => {
          console.error('Quote request: no se pudieron cargar las variantes', err);
          return [];
        });
    }
    return this.variantsPromise;
  }

  // Lee el id de variante actualmente seleccionado en el DOM (el
  // mismo input que usa el botón nativo de Add to cart) y refresca
  // el modal con los datos reales de esa variante.
  async syncToLiveVariant() {
    const scope = this.closest('[data-quote-scope]');
    const liveInput = scope?.querySelector('[ref="variantId"]');
    const liveVariantId = liveInput?.value;
    if (!liveVariantId) return;

    const variants = await this.getVariants();
    const variant = variants.find((v) => String(v.id) === String(liveVariantId));
    if (!variant) return;

    this.applyVariant(variant);
  }

  applyVariant(variant) {
    const priceFormatted = this.formatMoney(variant.price);
    const imageSrc = this.resizeImage(variant.featured_image?.src, 160);

    this.updateField('variant_title', variant.public_title || variant.title);
    this.updateField('sku', variant.sku);
    this.updateField('price', priceFormatted);
    if (imageSrc) this.updateField('image', imageSrc);

    this.setHidden('variant-id-input', variant.id);
    this.setHidden('variant-title-input', variant.public_title || variant.title);
    this.setHidden('sku-input', variant.sku);
    this.setHidden('price-input', priceFormatted);
    this.setHidden('image-input', imageSrc);
  }

  // Pide a la CDN de Shopify una versión reducida de la imagen en
  // vez de la resolución original (que puede pesar varios MB).
  resizeImage(src, width) {
    if (!src) return '';
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}width=${width}`;
  }

  // Lee la cantidad seleccionada AHORA MISMO en el selector -/+ de
  // la página de producto (input name="quantity", el mismo que usa
  // el botón nativo de Add to cart) y la copia al campo del modal.
  syncQuantity() {
    const scope = this.closest('[data-quote-scope]');
    const liveQtyInput = scope?.querySelector('input[name="quantity"]');
    const qtyField = this.querySelector('[data-quote-field="quantity"]');
    if (liveQtyInput && qtyField && liveQtyInput.value) {
      qtyField.value = liveQtyInput.value;
    }
  }

  updateField(key, value) {
    if (value == null) return;
    const el = this.querySelector(`[data-quote-field="${key}"]`);
    if (!el) return;
    if (el.tagName === 'IMG') el.src = value;
    else el.textContent = value;
  }

  setHidden(key, value) {
    const el = this.querySelector(`[data-quote-field="${key}"]`);
    if (el) el.value = value ?? '';
  }

  formatMoney(cents) {
    if (cents == null) return '';
    return (cents / 100).toLocaleString(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR',
    });
  }

  populateStaticFields(productData) {
    this.setHidden('title-input', productData.title);
    this.setHidden('url-input', productData.url);
    this.setHidden('product-id-input', productData.id);
    this.setHidden('metafields-input', JSON.stringify(productData.metafields || {}));
  }

  async handleSubmit(e) {
    e.preventDefault();
    const submitBtn = this.form.querySelector('.quote-modal__submit');
    submitBtn.disabled = true;

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
      });

      if (response.ok) {
        this.form.hidden = true;
        this.successEl.hidden = false;
      } else {
        throw new Error('Contact form submission failed');
      }
    } catch (err) {
      submitBtn.disabled = false;
      console.error('Quote request error:', err);
    }
  }
}

customElements.define('quote-request-modal', QuoteRequestModal);

// Delegación global: al pulsar el botón de solicitar presupuesto,
// primero se refresca el modal con la variante seleccionada AHORA
// MISMO, y solo entonces se abre.
document.addEventListener('click', async (e) => {
  const trigger = e.target.closest('[data-quote-modal-trigger]');
  if (!trigger) return;

  const modal = document.getElementById(trigger.getAttribute('aria-controls'));
  const modalComponent = modal?.querySelector('quote-request-modal');

  await modalComponent?.syncToLiveVariant();
  modalComponent?.syncQuantity();
  modal?.showModal();
});
